import { Grid2 as Grid, Typography } from '@mui/material'
import { xrayTypes } from 'pages/Diagnostics/constants/diagnostic_types'
import { useDiagnosisAgreement } from '../../context/DiagnosisAgreementContext'
import {
  XRayDiagnosticResultCard,
  XRayDiagnosticResultModal
} from './components'
import { IExamResponse } from 'types/exam/types'
import { XRayDiagnosticCodesKeys } from '../../../types/index'
import { useMemo, useState } from 'react'
import { useToast } from 'services/context/toast'
import { useOrganHighlights } from './useOrganHighlights'
import analytics from 'services/analytics'

interface ECGGridProps {
  examData: IExamResponse
}

export const XRayDiagnostics = ({ examData }: ECGGridProps) => {
  const { agreements, isLoading, handleAgreement } = useDiagnosisAgreement()
  const toast = useToast()
  const organHighlights = useOrganHighlights(examData)
  const [currentDiagnosticIndex, setCurrentDiagnosticIndex] = useState<
    number | null
  >(null)

  const handleDisagreeSuccess = () => {
    toast.handler({
      content: 'Obrigado! Seu feedback ajudará a melhorar o modelo de análise',
      severity: 'success',
      duration: 5000
    })
  }

  // Memoized diagnostics ordered by classificação (label_result_value) descendente:
  // provável (2) → suspeita (1) → não identificada (0). Empata pelo result_value desc.
  const diagnostics = useMemo(
    () =>
      XRayDiagnosticCodesKeys.map(key => {
        const result = examData.exam_results.find(r => r.result_type === key)
        const imageUrl = result?.link_result_data
          ? result.link_result_data
          : examData.link_original_data
        return {
          key,
          title: xrayTypes[key]?.name || key,
          image: imageUrl,
          resultValue: result?.result_value,
          labelResultValue: result?.label_result_value,
          ppvResultValue: result?.ppv_result_value,
          npvResultValue: result?.npv_result_value,
          id_exam_result: result?.id_exam_result
        }
      }).sort((a, b) => {
        const labelA = a.labelResultValue ?? 0
        const labelB = b.labelResultValue ?? 0
        if (labelB !== labelA) return labelB - labelA
        return (b.resultValue || 0) - (a.resultValue || 0)
      }),
    [examData.exam_results, examData.link_original_data]
  )

  const openFinding = (index: number, via: 'card' | 'modal_nav') => {
    setCurrentDiagnosticIndex(index)
    analytics.track('xray_finding_opened', {
      pathology: diagnostics[index].key,
      via
    })
  }

  const handleCardClick = (index: number) => {
    openFinding(index, 'card')
  }

  const handleNext = () => {
    if (
      currentDiagnosticIndex !== null &&
      currentDiagnosticIndex < diagnostics.length - 1
    ) {
      openFinding(currentDiagnosticIndex + 1, 'modal_nav')
    } else {
      openFinding(0, 'modal_nav') // Loop back to the first diagnostic
    }
  }

  const handlePrevious = () => {
    if (currentDiagnosticIndex !== null && currentDiagnosticIndex > 0) {
      openFinding(currentDiagnosticIndex - 1, 'modal_nav')
    } else {
      openFinding(diagnostics.length - 1, 'modal_nav') // Loop back to the last diagnostic
    }
  }

  const currentDiagnostic =
    currentDiagnosticIndex === null ? null : diagnostics[currentDiagnosticIndex]

  return (
    <Grid container mb={3}>
      <Grid size={12}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Análises:
        </Typography>
        <Grid container spacing={1}>
          {diagnostics.map((diagnostic, i) => (
            <Grid
              key={'result-card-' + i}
              size={{ xs: 12, sm: 3, md: 2 }}
              sx={{
                display: { xs: 'flex', md: 'block' },
                justifyContent: { xs: 'center' }
              }}
            >
              <XRayDiagnosticResultCard
                title={xrayTypes[diagnostic.key]?.name || diagnostic.key}
                image={diagnostic.image || ''}
                labelResultValue={diagnostic.labelResultValue}
                onClick={() => handleCardClick(i)}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>

      {currentDiagnostic && (
        <XRayDiagnosticResultModal
          title={
            xrayTypes[currentDiagnostic.key]?.name || currentDiagnostic.key
          }
          image={currentDiagnostic.image || ''}
          labelResultValue={currentDiagnostic.labelResultValue}
          ppvResultValue={currentDiagnostic.ppvResultValue}
          npvResultValue={currentDiagnostic.npvResultValue}
          imageId={currentDiagnostic.key}
          isOpen={currentDiagnosticIndex !== null}
          onClose={() => setCurrentDiagnosticIndex(null)}
          isDisagreed={
            currentDiagnostic.id_exam_result
              ? agreements[currentDiagnostic.key] === 'disagree'
              : undefined
          }
          onDisagree={async () => {
            const disagreed = agreements[currentDiagnostic.key] !== 'disagree'
            const saved = await handleAgreement(currentDiagnostic)
            if (saved) {
              analytics.track('xray_finding_disagreed', {
                pathology: currentDiagnostic.key,
                disagreed
              })
            }
          }}
          onDisagreeSuccess={handleDisagreeSuccess}
          loading={isLoading[currentDiagnostic.key]}
          onNext={handleNext}
          onPrevious={handlePrevious}
          hasNext={
            currentDiagnosticIndex !== null &&
            currentDiagnosticIndex < diagnostics.length - 1
          }
          hasPrevious={
            currentDiagnosticIndex !== null && currentDiagnosticIndex > 0
          }
          overlay={organHighlights.overlay}
          bottomPanel={organHighlights.bottomPanel}
        />
      )}
    </Grid>
  )
}
export default XRayDiagnostics
